import { AlertTriangle, Check, Copy, QrCode, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { getColorForCurrency } from "@/modules/dashboard/wallets/constants/walletColors.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaNetworkIcon, ColocaTokenIcon } from "@/shared/components/data-display/ColocaCryptoIcon";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import { useAddressQr } from "@/shared/hooks/useAddressQr";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { useCardDepositAddresses } from "../hooks/useCardDepositAddresses";
import type { CardDepositAddress, CardDepositAsset } from "../types/cards-api.types";
import {
  type CardDepositSubject,
  formatAssetList,
  formatDepositNetwork,
  hasCryptoDeposits,
  splitAddressForVerification,
} from "../utils/cardDeposit";

const MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";
const COPIED_FEEDBACK_MS = 2000;

export interface CardDepositPanelProps {
  card: CardDepositSubject;
}

/**
 * Container: decides whether the card can take crypto deposits at all and hands the backend's answer to
 * `CardDepositPanelView`. The feature opens organization by organization, and only the backend knows whether
 * it is open for this card: it answers 403 `card_deposits_not_enabled` before anything else (staff included),
 * then 409 for a card that is not ACTIVE. So the read is always made, and nothing renders until it answers —
 * an organization without the feature must not see a deposit panel, not even a skeleton of one.
 */
export function CardDepositPanel({ card }: CardDepositPanelProps) {
  const supported = hasCryptoDeposits(card);
  const { addresses, isLoading, error, notEnabled, notActive, hasSettled, refresh } = useCardDepositAddresses(
    card.id,
    { enabled: supported },
  );

  if (!supported || notEnabled || !hasSettled) return null;

  return (
    <CardDepositPanelView
      addresses={addresses}
      isLoading={isLoading}
      hasError={error !== null}
      isCardActive={!notActive}
      onRetry={() => void refresh()}
    />
  );
}

export interface CardDepositPanelViewProps {
  addresses: CardDepositAddress[];
  isLoading: boolean;
  /**
   * The read failed. A flag rather than the message on purpose: an upstream error can carry the issuer's own
   * prose, and provider identity never reaches the screen — the panel says what happened in its own words.
   */
  hasError: boolean;
  /** False for a frozen, disabled or pending card: deposits are refused, so nothing was fetched. */
  isCardActive: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * Presentational deposit panel — a pure function of its props (no fetching), so every state has a story. An
 * empty address list renders nothing: the card simply has no crypto way in.
 */
export function CardDepositPanelView({
  addresses,
  isLoading,
  hasError,
  isCardActive,
  onRetry,
  className,
}: CardDepositPanelViewProps) {
  const { t } = useTranslations("cards");

  if (!isCardActive) {
    return (
      <ColocaSurface variant="muted" padding="lg" className={className}>
        <h3 className="text-sm font-medium text-foreground">{t("card_deposit_title")}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{t("card_deposit_inactive")}</p>
      </ColocaSurface>
    );
  }

  const hasAddresses = addresses.length > 0;
  if (!hasAddresses && !isLoading && !hasError) return null;

  return (
    <ColocaSurface padding="lg" aria-busy={isLoading || undefined} className={className}>
      <h3 className="text-sm font-medium text-foreground">{t("card_deposit_title")}</h3>
      <p className="text-[11px] text-muted-foreground">{t("card_deposit_subtitle")}</p>

      {hasAddresses ? (
        <div className="divide-y divide-border/60">
          {addresses.map((entry) => (
            <DepositAddressBlock key={`${entry.network}:${entry.address}`} entry={entry} />
          ))}
        </div>
      ) : isLoading ? (
        <DepositAddressSkeleton />
      ) : (
        <ColocaAlert
          variant="destructive"
          icon={AlertTriangle}
          className="mt-4"
          text={t("card_deposit_error")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={onRetry}>
              {t("card_deposit_retry")}
            </ColocaButton>
          }
        />
      )}
    </ColocaSurface>
  );
}

function DepositAddressBlock({ entry }: { entry: CardDepositAddress }) {
  const { t, activeLanguage } = useTranslations("cards");
  const network = formatDepositNetwork(entry.network);
  const { head, middle, tail } = splitAddressForVerification(entry.address);
  const qr = useAddressQr(entry.address);
  const { copied, copy } = useCopyAddress(entry.address, t("card_deposit_copied_toast"));
  const symbols = entry.assets.map((asset) => asset.symbol);

  return (
    <div className="space-y-4 py-4 last:pb-0">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        {/* The box is reserved before the image exists: encoding takes a moment, and a panel that grows under
            the reader's eyes is worse than one that starts at its final size. */}
        <div className="mx-auto h-40 w-40 shrink-0 overflow-hidden rounded-xl border border-border sm:mx-0">
          {qr.pending ? (
            <ColocaSkeleton className="h-full w-full rounded-none" />
          ) : qr.src ? (
            <img
              src={qr.src}
              alt={t("card_deposit_qr_alt", { network })}
              className="h-full w-full object-contain"
            />
          ) : (
            // Encoding failed (non-fatal): the address below is still copyable.
            <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
              <QrCode className="size-8" aria-hidden="true" />
            </div>
          )}
        </div>

        <dl className="min-w-0 flex-1 space-y-4">
          <div>
            <dt className={MICRO_LABEL}>{t("card_deposit_network_label")}</dt>
            <dd className="mt-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
              <ColocaNetworkIcon network={entry.network} size={20} />
              <span>{network}</span>
            </dd>
          </div>

          <div>
            <dt className={MICRO_LABEL}>{t("card_deposit_address_label")}</dt>
            <dd className="mt-1.5 space-y-2.5">
              {/* Always whole, never truncated. The ends a person checks against their wallet are set in
                  full weight; the middle recedes but stays readable. */}
              <p className="break-all font-mono text-sm leading-relaxed text-foreground">
                <span className="font-semibold">{head}</span>
                <span className="text-foreground/60">{middle}</span>
                <span className="font-semibold">{tail}</span>
              </p>
              <ColocaButton
                variant="secondary"
                size="sm"
                icon={copied ? Check : Copy}
                onClick={() => void copy()}
              >
                {copied ? t("card_deposit_copied") : t("card_deposit_copy")}
              </ColocaButton>
            </dd>
          </div>

          {entry.assets.length > 0 && (
            <div>
              <dt className={MICRO_LABEL}>{t("card_deposit_assets_label")}</dt>
              <dd className="mt-1.5 flex flex-wrap gap-1.5">
                {entry.assets.map((asset) => (
                  <DepositAssetChip key={asset.symbol} asset={asset} />
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {symbols.length > 0 && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          text={t("card_deposit_warning", { assets: formatAssetList(symbols, activeLanguage), network })}
        />
      )}
    </div>
  );
}

function DepositAssetChip({ asset }: { asset: CardDepositAsset }) {
  const { t } = useTranslations("cards");
  return (
    <ColocaTooltip
      triggerRender={
        <button
          type="button"
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-background pl-1.5 pr-2.5",
            "font-mono text-xs font-medium text-foreground transition-colors hover:bg-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <ColocaTokenIcon
            symbol={asset.symbol}
            size={16}
            fallback={
              <span
                aria-hidden="true"
                className="ml-1 size-2 rounded-full"
                style={{ backgroundColor: getColorForCurrency(asset.symbol) }}
              />
            }
          />
          {asset.symbol}
        </button>
      }
    >
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{asset.name}</span>
        {asset.contract_address && (
          <span className="break-all font-mono text-[10px] opacity-80">
            {t("card_deposit_asset_contract", { address: asset.contract_address })}
          </span>
        )}
      </span>
    </ColocaTooltip>
  );
}

function DepositAddressSkeleton() {
  return (
    <div className="flex flex-col gap-5 pt-4 sm:flex-row sm:items-start" aria-hidden="true">
      <ColocaSkeleton className="mx-auto h-40 w-40 shrink-0 rounded-xl sm:mx-0" />
      <div className="min-w-0 flex-1 space-y-4">
        <div className="space-y-1.5">
          <ColocaSkeleton className="h-2.5 w-12" />
          <ColocaSkeleton className="h-5 w-20" />
        </div>
        <div className="space-y-1.5">
          <ColocaSkeleton className="h-2.5 w-28" />
          <ColocaSkeleton className="h-4 w-full" />
          <ColocaSkeleton className="h-8 w-36" />
        </div>
        <div className="space-y-1.5">
          <ColocaSkeleton className="h-2.5 w-24" />
          <div className="flex gap-1.5">
            <ColocaSkeleton className="h-7 w-16 rounded-full" />
            <ColocaSkeleton className="h-7 w-16 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Copy → Check for two seconds, plus a toast. A denied clipboard is silent: the address is on screen. */
function useCopyAddress(address: string, toastMessage: string) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(address);
    } catch {
      return;
    }
    setCopied(true);
    colocaToast.success(toastMessage);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  }, [address, toastMessage]);

  return { copied, copy };
}
